import ReportSubSection from '@components/ReportSubSection';
import { Row } from '@simplywallst/ui-core';
import { NewsItemWrapper, NewsDateAndProvider, NewsTitle } from './styled';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import { COMPANY_NEWS } from '@/constants/feedbackSections';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { QUERY_KEY, queryFn } from './query';
import loadable from '@loadable/component';
import ContentLoader from 'react-content-loader';
import { useTheme } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import If from '@components/If';
import { shallowEqual, useSelector } from 'react-redux';
import type {
  State as CompanyState,
  Summary,
} from '@/pages/CompanyReport/redux/interface';

const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);

interface Props {
  id?: string;
  uniqueSymbol?: string;
}
export function RelatedNews(props: Props) {
  const { trackEvent } = useTracking();
  const theme = useTheme();
  const summary = useSelector<
    { company: CompanyState },
    Record<string, Summary>
  >((state) => state.company.summary, shallowEqual);
  const canonicalUrl = props.id ? summary[props.id]?.canonicalUrl : '';

  const { data, status } = useQuery({
    queryKey: [...QUERY_KEY, canonicalUrl],
    queryFn,
    enabled: Boolean(canonicalUrl),
    placeholderData: keepPreviousData,
    staleTime: 3600,
    select: (response) => {
      const { data } = response;
      return data
        .map((article) => {
          return {
            key: article.date,
            author: article.provider_name,
            publishDate: article.date_diff_friendly,
            url: article.url,
            title: article.headline,
          };
        })
        .slice(0, 3);
    },
  });

  return (
    <ReportSubSection
      title="Simply Wall St News"
      body={
        <>
          <Row>
            <If
              conditions={{
                loading: status === 'pending',
                success: status === 'success',
              }}
            >
              {{
                loading: () => (
                  <ContentLoader
                    foregroundColor={theme.color.spaceGray}
                    backgroundColor={theme.color.darkGray}
                    height={142}
                  >
                    <rect x="0" y="0" rx="4" ry="4" width="220" height="12" />
                    <rect x="0" y="25" rx="4" ry="4" width="220" height="250" />
                  </ContentLoader>
                ),
                success: () =>
                  typeof data !== 'undefined' && data.length > 0 ? (
                    data.map((article, index) => {
                      return (
                        <NewsItemWrapper
                          col={12}
                          colMd={4}
                          key={article.key}
                          data-cy-id="news-item"
                        >
                          <NewsDateAndProvider>
                            {`${article.publishDate} | ${article.author}`}
                          </NewsDateAndProvider>
                          <NewsTitle
                            as="a"
                            styleType="softLink"
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              trackEvent({
                                action: 'click',
                                url: article.url,
                                listIndex: index,
                              });
                            }}
                          >
                            {article.title}
                          </NewsTitle>
                        </NewsItemWrapper>
                      );
                    })
                  ) : (
                    <p>No news available</p>
                  ),
              }}
            </If>
          </Row>
          <DeferRenderUntilIntersecting>
            {() => (
              <ChartActionsContainer
                companyName={props.uniqueSymbol}
                feedbackLabel={COMPANY_NEWS}
              />
            )}
          </DeferRenderUntilIntersecting>
        </>
      }
    />
  );
}
